import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  SimpleSidebar,
  SimpleSidebarContent,
  SimpleSidebarLayout,
} from "@/core/components/ui/simple-sidebar";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { PageHeader } from "@/business-settings/common/page-header";
import useAuth from "@/core/auth/use-auth";
import { useBusiness } from "@/core/business";
import { formatDateRange } from "@/core/utils/date";
import { IcmLineItemSidebar } from "@/icm/common";
import { useGetIcmStatementDetail } from "@/icm/query";
import { TechnicianStatementPage } from "@/icm/technician-payroll";
import {
  getPayBreakdownPath,
  getPayBreakdownStatementPath,
} from "@/payroll/pay-breakdown/routes";
import { isTech } from "@/user/utils";
import { IcmStatementRuleTable } from "./statement-rule-table";
import { IcmStatementSummaryCard } from "./statement-summary-card";
import { groupLineItemsByRule } from "./utils";

const AdminIcmStatementPage = () => {
  const { statementId, periodStart } = useParams<{
    statementId: string;
    periodStart: string;
  }>();
  const navigate = useNavigate();
  const { businessId, selectedBusiness } = useBusiness();
  const { data: statement } = useGetIcmStatementDetail(businessId, statementId);

  const [selectedLineItemId, setSelectedLineItemId] = useState<string | null>(
    null,
  );

  const allItems = statement?.line_items ?? [];
  const jobItems = allItems.filter((item) => item.job_id != null);
  const byRule = useMemo(() => groupLineItemsByRule(jobItems), [jobItems]);

  const selectedLineItem = useMemo(
    () =>
      selectedLineItemId
        ? ((statement?.line_items ?? []).find(
            (item) => item.id === selectedLineItemId,
          ) ?? null)
        : null,
    [selectedLineItemId, statement?.line_items],
  );

  const statementPeriodStart = periodStart ?? statement?.period_start;

  useEffect(() => {
    if (!periodStart && statementId && statement?.period_start) {
      navigate(
        getPayBreakdownStatementPath(statement.period_start, statementId),
        {
          replace: true,
        },
      );
    }
  }, [navigate, periodStart, statement?.period_start, statementId]);

  return (
    <SimpleSidebarLayout
      open={!!selectedLineItem}
      onOpenChange={(open) => !open && setSelectedLineItemId(null)}
    >
      <SimpleSidebarContent>
        <Stack
          fullWidth
          className="mx-auto max-w-[85rem] overflow-x-hidden px-6 py-8"
          gap="xl"
        >
          <Stack
            direction="row"
            align="center"
            justify="between"
            fullWidth
            gap="md"
          >
            <Stack gap="xs">
              <button
                type="button"
                onClick={() =>
                  navigate(getPayBreakdownPath(statementPeriodStart))
                }
                className="flex w-fit items-center gap-1 text-xs text-secondary-text hover:text-primary"
              >
                <ArrowLeftIcon className="h-2.5 w-2.5" />
                Grid
              </button>
              <PageHeader>
                {statement?.technician_name ?? "Technician"}
              </PageHeader>
              {statement && (
                <Typography variant="bodyW3" textColor="secondary">
                  {selectedBusiness?.name} ·{" "}
                  {formatDateRange(
                    statement.period_start,
                    statement.period_end,
                  )}
                </Typography>
              )}
            </Stack>
          </Stack>
          {statement && (
            <>
              <IcmStatementSummaryCard
                statement={statement}
                isSidebarOpen={!!selectedLineItem}
              />
              <Stack gap="xl" fullWidth>
                {Object.entries(byRule).map(([ruleName, items]) => (
                  <IcmStatementRuleTable
                    key={ruleName}
                    ruleName={ruleName}
                    items={items}
                    selectedLineItemId={selectedLineItem?.id ?? null}
                    onSelectLineItem={(item) => setSelectedLineItemId(item.id)}
                  />
                ))}
              </Stack>
            </>
          )}
        </Stack>
      </SimpleSidebarContent>

      <SimpleSidebar>
        {selectedLineItem && (
          <IcmLineItemSidebar
            lineItem={selectedLineItem}
            onClose={() => setSelectedLineItemId(null)}
            businessId={businessId}
            statementId={statementId}
          />
        )}
      </SimpleSidebar>
    </SimpleSidebarLayout>
  );
};

const IcmStatementPage = () => {
  const { user } = useAuth();

  if (isTech(user?.role)) {
    return <TechnicianStatementPage />;
  }

  return <AdminIcmStatementPage />;
};

export default IcmStatementPage;
